import React from 'react';
import PageHeader from '../components/PageHeader';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const Home = () => {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="prose-custom">
      <PageHeader 
        title="A Gift to Your Future Self" 
        subtitle="Reproducibility isn't just about moral scientific integrity—it's a practical property of the whole research lifecycle."
      />

      <p className="text-lg">
        Whether you are a bench scientist, a clinical researcher, or a computational biologist, 
        you've likely heard about the "reproducibility crisis." But knowing that reproducibility 
        matters is very different from knowing <strong>where to start</strong>. 
      </p>

      <h2>The Evidence Base & The Nuance</h2>
      <p>
        Large-scale replication efforts (like the Reproducibility Project in Psychology and Cancer Biology) 
        and researcher surveys (such as Nature's 2016 poll where 90% of respondents agreed there was a crisis) 
        have highlighted widespread challenges. The cost of irreproducibility is measured in billions of dollars 
        and years of wasted trainee effort.
      </p>
      <p>
        However, we must balance this view. Science is inherently difficult, and exploratory research 
        <em>should</em> push boundaries where failure is an option. The goal is not zero variation, 
        but rather avoiding complacency while steering clear of crisis-hype.
      </p>

      <h2>The Whole Lifecycle Framing</h2>
      <p>
        Many scientists correctly associate reproducibility with computational analysis (e.g., sharing code). 
        While true, this is only the final act. <strong>Reproducibility is a property of the entire research lifecycle.</strong> 
        It starts upstream at experimental design and flows downstream through data collection, analysis, and archiving.
      </p>

      <div className="grid md:grid-cols-3 gap-6 my-10">
        {[
          { num: '1', title: 'Design', path: '/design', desc: 'Pre-registration, power analysis, and robust planning.' },
          { num: '2', title: 'Data', path: '/data', desc: 'FAIR principles, metadata, and lab notebooks.' },
          { num: '3', title: 'Analysis', path: '/analysis', desc: 'Version control, containers, and workflow managers.' },
          { num: '4', title: 'Reporting', path: '/reporting', desc: 'TOP guidelines, transparent methods, and null results.' },
          { num: '5', title: 'Sharing', path: '/sharing', desc: 'Persistent repositories, DOIs, licensing, and credit.' }
        ].map(stage => (
          <Link key={stage.num} to={stage.path} className="block group">
            <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:border-brand-300 transition-all h-full">
              <span className="text-brand-500 font-bold text-sm mb-2 block">Stage {stage.num}</span>
              <h3 className="text-lg font-semibold mt-0 mb-2 group-hover:text-brand-700">{stage.title}</h3>
              <p className="text-sm text-slate-600 m-0">{stage.desc}</p>
            </div>
          </Link>
        ))}
        <Link to="/getting-started" className="block group">
          <div className="bg-brand-50 p-6 rounded-xl border border-brand-200 shadow-sm hover:shadow-md hover:border-brand-400 transition-all h-full flex flex-col justify-center">
            <span className="text-brand-600 font-bold text-sm mb-2 block">Start Here</span>
            <h3 className="text-lg font-semibold mt-0 mb-2 group-hover:text-brand-800">Getting Started &rarr;</h3>
            <p className="text-sm text-slate-600 m-0">A realistic, tiered checklist—one thing you can do this week.</p>
          </div>
        </Link>
      </div>

      <p>
        By adopting these practices, you are not just satisfying grant requirements or reviewer demands. 
        You are making your research smoother, onboarding new lab members faster, and ensuring that you 
        can trust your own results six months from now.
      </p>
    </motion.div>
  );
};

export default Home;